<template>
  <div class="candidate profile financial-assessment">
    <can I="read" a="Profile">
      <v-toolbar class="transparent elevation-0 my-3">
        <div class="headline">Financial Assessment</div>
      </v-toolbar>
      <v-card class="mt-2">
        <v-card-title class="subheading">Household Income</v-card-title>
        <table class="v-table theme--dark">
          <tbody>
            <tr>
              <th class="text-xs-right">Currency</th>
              <td>{{ currency.long }}</td>
            </tr>
            <tr>
              <th class="text-xs-right">Dependents</th>
              <td>{{ financial.dependents }}</td>
            </tr>
          </tbody>
        </table>

        <table v-for="income in financial.incomes" class="v-table theme--dark" :key="`income-${income.id}`">
          <tbody class="py-3">
            <can I="edit" a="Profile">
              <div class="my-5">
                <v-btn fab right absolute @click.stop="edit = income" color="primary" style="margin-top: -3rem;">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </div>
            </can>
            <tr>
              <th class="text-xs-right">
                {{ income.relationship }}
              </th>
              <td>{{ income.givenName }} {{ income.familyName }}</td>
            </tr>
            <tr>
              <th class="text-xs-right">Occupation</th>
              <td>{{ income.occupation }}</td>
            </tr>
            <tr>
              <th class="text-xs-right">Gross Income</th>
              <td>
                {{
                  income.grossIncome
                  | currency(currency.short, 0, {
                    thousandsSeparator: ' ',
                    spaceBetweenAmountAndSymbol: true,
                  })
                }}
                p/m
              </td>
            </tr>
            <tr>
              <th class="text-xs-right">Documentation</th>
              <td>
                <a v-if="income.proof" :href="income.proof.url" target="_blank">
                  View {{ income.typeOfProof }}
                </a>
                <span v-else>Missing {{ income.typeOfProof }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        <v-card-actions>
          <v-spacer />
          <can I="delete" a="FinancialAssessment" v-if="financial.id">
            <v-dialog v-model="dialog" lazy absolute max-width="600">
              <v-btn flat slot="activator" color="error"> <v-icon left>mdi-delete</v-icon> Delete </v-btn>
              <v-card dark>
                <v-card-title class="headline">
                  Are you sure?
                </v-card-title>
                <v-card-text>
                  This action cannot be undone and will remove all houshold incomes for this profile.
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn flat color="grey" @click.native="dialog = false">cancel</v-btn>
                  <v-btn color="error" @click="destroy">Delete</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </can>
        </v-card-actions>
      </v-card>

      <v-dialog persistent v-if="financial.currency" :value="edit && edit.id" max-width="70%">
        <v-card>
          <v-card-title class="title">
            Editing household member
          </v-card-title>
          <v-form ref="form" v-model="valid">
            <v-card-text v-if="edit.relationship">
              <v-select label="Relationship" v-model="edit.relationship" :items="relationships" :rules="[rules.required]"
                required />
              <v-text-field label="First Name" v-model="edit.givenName" :rules="[rules.required, rules.counter]" required
                counter="32" />
              <v-text-field label="Surname" v-model="edit.familyName" :rules="[rules.required, rules.counter]" required
                counter="32" />
              <v-text-field label="Occupation" v-model="edit.occupation" :rules="[rules.required, rules.counter]" required
                counter="32" />
              <v-text-field label="Gross Monthly Income" v-model="edit.grossIncome" type="number" :prefix="currency.short"
                :rules="[rules.requiredNumber]" required hint="Income before taxes and expenses" />
              <v-select label="Type of Proof" v-model="edit.typeOfProof" :items="proofs" :rules="[rules.required]"
                required />

              <DocumentUpload v-if="edit && edit.id" :action="`/admin/household_incomes/${edit.id}/proof`"
                :label="edit.typeOfProof" :value="edit.proof" @submit="close" auto-submit />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn flat color="grey" @click.stop="edit = {}">
                Cancel
              </v-btn>
              <v-btn class="primary" :disabled="!valid" @click="save" :loading="saving">
                Save
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-dialog>
    </can>
  </div>
</template>

<script>
// Components
import DocumentUpload from '@/components/DocumentUpload'

// Data
import currencies from '@/data/currencies'
const relationships = ['Father', 'Mother', 'Guardian', 'Grandfather', 'Grandmother']
const proofs = ['Affidavit', 'Death Certificate', 'Income Statement', 'Payslip']

export default {
  components: { DocumentUpload },

  props: { profile: { type: Object, required: true } },

  data() {
    return {
      // Validation
      valid: false,
      rules: {
        required: v => !!v || 'must be provided',
        requiredNumber: v => v === 0 || !!v || 'must be provided',
        counter: value => value.length <= 32 || 'Max 32 characters',
      },
      edit: {},
      saving: false,
      currencies,
      relationships,
      proofs,
      dialog: false
    }
  },

  computed: {
    currency() {
      if (!this.financial && this.financial.currency) return

      const currency = this.currencies[this.financial.currency]

      return {
        long: currency ? `${currency.name} (${currency.iso.code})` : '',
        short: currency ? currency.symbol.default.display : '',
      }
    },

    financial() {
      if (!this.profile.financial) return {}

      return this.profile.financial
    },
  },
  methods: {
    destroy() {
      this.$store.dispatch('admin/financialAssessment/destroy', this.financial.id)
        .finally(
          () => {
            this.$emit('refresh')
          }
        )
    },
    close() {
      this.$emit('refresh')
      this.edit = {}
    },

    save() {
      if (this.$refs['form'].validate() && this.valid && this.edit.id) {
        this.saving = true
        this.$store
          .dispatch('admin/householdIncome/update', this.edit)
          .then(
            () => {
              this.$emit('refresh')
              this.$store.dispatch('snackbarSuccess', `${this.edit.relationship} Saved`)
            },
            () => this.$store.dispatch('snackbarError', 'Failed to Save')
          )
          .finally(() => {
            this.saving = false
            this.edit = {}
          })
      }
    },
  },
}
</script>
